import { ActionButton } from '../components/ActionButton';
import { useControllerConnectionStatus } from '../Hooks/useControllerConnectionStatus';
import { ReactComponent as CropSteeringP0 } from '../Images/crop_steering_v2_p0.svg';
import { ReactComponent as CropSteeringP1 } from '../Images/crop_steering_v2_p1.svg';
import { ReactComponent as CropSteeringP2 } from '../Images/crop_steering_v2_p2.svg';
import { ReactComponent as CropSteeringP3 } from '../Images/crop_steering_v2_p3.svg';

import React, { useState, useEffect, useRef } from 'react';
import TabContext from '@material-ui/lab/TabContext';
import TabList from '@material-ui/lab/TabList';
import TabPanel from '@material-ui/lab/TabPanel';
import Box from '@material-ui/core/Box';
import { AppBar, Tab, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import MuiSwitch from '../FormWidgets/SettingsSwitch';
import IrrigationEvent from '../FormWidgets/IrrigationEvent';
import IrrigationActions from './Irrigation/IrrigationActions';
import RootZoneAddressSetup from './Irrigation/RootZoneAddressSetup';

import DialogWithEnterAction from '../components/DialogWithEnterAction';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import EditIcon from '@material-ui/icons/Edit';

import { useSnackbar } from 'notistack';

import { ShowSetting, ShowSettingMinMax } from '../FormWidgets/ShowSliderSetting';
import { ExpandableSettingsGroupGrid, SettingGroupGrid, ValueBox } from '../FormWidgets/SettingGroup';
import { HandleAction, isZoned } from '../Utils/utils.js';
import { LoadSettingsDialog } from '../Utils/load_settings';
import Desc, { GetHelp, InfoButton } from '../FormWidgets/Description';

import { settingControls, useSavedSettings, writeTempSettings } from './settingFunctions';
import { useParams } from 'react-router-dom';
import { useSnapshot } from 'valtio';
import getStateManager from '../StateManager';
import { SettingActions } from '../components';
import { useIsMobile } from '../Hooks/useMedia';
import { useTranslation } from 'react-i18next';
import { irrigationStrategyMapping, substrateSelectionMapping } from '../Constants/constants';
import { irrigationAdaptiveDefaults, irrigationPresetMapping, irrigationPresets } from '../Constants/IrrigationPresets.js';
import {
  supportsIrrigationShotTimeoutSeconds,
  supportsAdaptiveIrrigation,
  supportsIrrigationReferenceInterval,
  IRRIGATION_ADAPTIVE_FIELDS,
  IRRIGATION_P2_SOAK_MIN_SECONDS,
  IRRIGATION_P2_SOAK_MAX_SECONDS,
  IRRIGATION_P2_TOTAL_MAX_SECONDS,
  normalizeIrrigationAdaptiveDurations,
  irrigationShotTimeoutKey,
  importIrrigationTimeouts,
  validIrrigationTimeouts,
  reconcileIrrigationDraft,
  restoreIrrigationDraft,
  irrigationEndTimeForWire,
  isIrrigationAdvancedVwcStrategy,
  isIrrigationAutoSimpleStrategy,
  isIrrigationAutoStrategy,
  isIrrigationTimeStrategy,
  isIrrigationVwcSimpleStrategy,
  isIrrigationVwcStrategy,
  normalizeP1MinimumDurationMinutes,
  P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES,
  supportsIrrigationTankInfo,
  supportsModernIrrigationStrategies,
  supportsP1MinimumCumulativeIrrigationTime
} from '../Utils/irrigation_utils.js';

import { EventGapSelect, FlexSelect } from '../FormWidgets/Select';
import { isTankEnabled } from '../Utils/tank_availability';

import _, { parseInt } from 'lodash';
import { parse } from 'date-fns';

const useStyles = makeStyles((theme) => ({
  cropSteering: {
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginLeft: '0.5%'
    },
    [theme.breakpoints.up('md')]: {
      width: '200%',
      marginLeft: '0.5%'
    }
  }
}));

// SPI enum values from spi_commandESP32.h
const GCSPI_IRRIGATION_TIME_PHASE_P1 = 1;
const GCSPI_IRRIGATION_TIME_PHASE_P2 = 2;
const GCSPI_IRRIGATION_MAX_EVENTS = 64;
const P1_EVENT_START_MAX_MINUTES = 4 * 60;
const INDUSTRIAL_IRRIGATION_CONTROLLER_TYPES = [5155]; // 6000 does not support the current irrigation system
const SHOW_IRRIGATION_EVENTS_ERROR_BANNER = true;
const P1_WAIT_AFTER_SHOT_MINUTES = 'irrigation_p1_wait_after_shot_minutes';
const P1_VWC_AUTO_SP_NO_INCREASE_SECONDS =
  'irrigation_P1_vwc_auto_sp_max_duration_no_increase_seconds';
const P2_WAIT_AFTER_SHOT = 'irrigation_p2_wait_after_shot_s';
const P2_ADAPTIVE_DURATION_FIELDS = [
  'irrigation_p2_shot_duration_timeout_seconds',
  'irrigation_p2_refill_soak_seconds',
  'irrigation_p2_max_total_watering_seconds'
];

function getMinutesDifference(endMinutes, startMinutes) {
  if (endMinutes >= startMinutes) {
    return endMinutes - startMinutes;
  }
  // crossing midnight
  return 24 * 60 - startMinutes + endMinutes;
}

function durationSecondsToRoundedMinutes(durationSeconds) {
  if (durationSeconds <= 0) return 0;
  return Math.ceil(durationSeconds / 60);
}

function isP1EventStartOutOfBounds(event) {
  if (!event || Number(event.phase) !== GCSPI_IRRIGATION_TIME_PHASE_P1) return false;

  const startMinute = Number(event.start_minute_from_phase_begin);
  return !Number.isFinite(startMinute) || startMinute < 0 || startMinute > P1_EVENT_START_MAX_MINUTES;
}

export default function MultipleIrrigationSettings() {
  const { t } = useTranslation();
  const stateManager = getStateManager();
  const params = useParams();
  const { controller, parent } = stateManager.getControllerForTab(params);
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  const receivedSettings = useSnapshot(parent.settings);
  const [storedValues, setValues] = useState(() => _.cloneDeep(parent.settings.irrigation));
  const values = normalizeIrrigationAdaptiveDurations(storedValues, controller);
  const draftActive = useRef(false);
  const baseControls = settingControls(controller, parent, 'irrigation', values, setValues, {
    canEdit: () => Boolean(controller.access_rights?.nutrition?.write)
  });

  const secondsSupported = supportsIrrigationShotTimeoutSeconds(controller);
  const P1_SHOT_DURATION_TIMEOUT = irrigationShotTimeoutKey(1, secondsSupported);
  const P2_SHOT_DURATION_TIMEOUT = irrigationShotTimeoutKey(2, secondsSupported);
  const P3_SHOT_DURATION_TIMEOUT = irrigationShotTimeoutKey(3, true);
  const timeoutToMinutes = (value) => (secondsSupported ? Math.ceil(value / 60) : value);
  const pendingSubmission = useRef(null);

  const adaptiveSupported = (index) => supportsAdaptiveIrrigation(
    controller, parent.settings.irrigation.irrigation_settings?.[index]
  );

  function getP1ShotDurationTimeoutMin(irrigation, index) {
    const minimum = secondsSupported ? 10 : 1;
    if (adaptiveSupported(index)) return minimum;
    if (!isIrrigationAutoStrategy(irrigation?.irrigation_strategy)) return minimum;
    return Math.max(
      minimum,
      secondsSupported
        ? Number(irrigation?.[P1_VWC_AUTO_SP_NO_INCREASE_SECONDS]) || 0
        : durationSecondsToRoundedMinutes(Number(irrigation?.[P1_VWC_AUTO_SP_NO_INCREASE_SECONDS]) || 0)
    );
  }

  function getEffectiveP1ShotDurationTimeout(irrigation, index) {
    return Math.max(
      secondsSupported ? 10 : 1,
      Number(irrigation?.[P1_SHOT_DURATION_TIMEOUT]) || 0,
      getP1ShotDurationTimeoutMin(irrigation, index)
    );
  }

  function getP2ShotDurationTimeout(irrigation) {
    return Math.max(secondsSupported ? 10 : 1, Number(irrigation?.[P2_SHOT_DURATION_TIMEOUT]) || 0);
  }

  const adaptiveRefillEnabled = (irrigation, index) =>
    adaptiveSupported(index) && irrigation.irrigation_p2_adaptive_enabled === 1;

  function getP2ShotDurationTimeoutMax(irrigation, index) {
    if (adaptiveRefillEnabled(irrigation, index) &&
        isIrrigationAdvancedVwcStrategy(irrigation.irrigation_strategy)) {
      return Math.max(10, Math.min(IRRIGATION_P2_TOTAL_MAX_SECONDS,
        irrigation.irrigation_p2_max_total_watering_seconds));
    }
    return secondsSupported ? 3600 : 60;
  }

  const getP2WaitMinimumMinutes = (irrigation, index) => adaptiveRefillEnabled(irrigation, index)
    ? Math.max(1, Math.ceil(irrigation.irrigation_p2_refill_soak_seconds / 60))
    : timeoutToMinutes(getP2ShotDurationTimeout(irrigation));

  const controls = {
    ...baseControls,
    setValue: (...args) => {
      draftActive.current = true;
      baseControls.setValue(...args);
    },
    restoreTempSettings: () => {
      const draft = _.cloneDeep(parent.temp_settings.irrigation ?? {});
      for (const [index, irrigation] of Object.entries(draft.irrigation_settings ?? {})) {
        if (irrigation == null) continue;
        const imported = importIrrigationTimeouts(irrigation, controller);
        if (!imported) return;
        draft.irrigation_settings[index] = imported;
      }
      draftActive.current = true;
      parent.temp_settings.irrigation = draft;
      writeTempSettings(parent);
      setValues(restoreIrrigationDraft(parent.settings.irrigation, draft));
    },
    resetSettings: () => {
      pendingSubmission.current = null;
      draftActive.current = false;
      baseControls.resetSettings();
    }
  };

  const { openLoadSettingsDialog, closeLoadSettingsDialog } =
    useSavedSettings(parent, controls, 'irrigation');

  useEffect(() => {
    if (pendingSubmission.current) {
      const remaining = reconcileIrrigationDraft(
        parent.temp_settings.irrigation ?? {},
        pendingSubmission.current,
        receivedSettings.irrigation
      );
      if (_.isEmpty(remaining)) {
        delete parent.temp_settings.irrigation;
        pendingSubmission.current = null;
      } else parent.temp_settings.irrigation = remaining;
      writeTempSettings(parent);
    }
    setValues(restoreIrrigationDraft(
      receivedSettings.irrigation,
      draftActive.current ? parent.temp_settings.irrigation ?? {} : {}
    ));
  }, [parent, receivedSettings.irrigation]);

  let { enqueueSnackbar } = useSnackbar();
  const mobile = useIsMobile();
  const classes = useStyles();
  const modernIrrigationStrategiesSupported = supportsModernIrrigationStrategies(controller);
  const irrigationTankInfoSupported = supportsIrrigationTankInfo(controller);
  const p1MinimumCumulativeTimeSupported =
    supportsP1MinimumCumulativeIrrigationTime(controller);
  const irrigationTankInfo = Array.isArray(parent.settings.irrigation_tank_info)
    ? parent.settings.irrigation_tank_info
    : [];
  const availableIrrigationStrategies = modernIrrigationStrategiesSupported
    ? irrigationStrategyMapping
    : irrigationStrategyMapping.filter(([strategy]) =>
        isIrrigationAdvancedVwcStrategy(strategy)
      );

  const transform_sent_settings = (settings) => {
    // Fail closed at the final payload boundary as well as in the save-button handler.
    // Returning null skips update_setpoints entirely.
    if (
      !controller.access_rights?.nutrition?.write ||
      !validIrrigationTimeouts(settings, controller) ||
      (settings.irrigation_events || []).some(isP1EventStartOutOfBounds)
    ) return null;

    // Convert irrigation_p2_end_before_sunset from minutes to a string format "HH:MM:SS"
    settings.irrigation_settings.forEach((irrigation, index) => {
      if (!supportsIrrigationReferenceInterval(controller)) {
        delete irrigation.irrigation_p1_auto_vwc_exec_gap_days;
      }
      if (!adaptiveSupported(index)) {
        IRRIGATION_ADAPTIVE_FIELDS.forEach((key) => delete irrigation[key]);
      }
      irrigation[P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES] =
        normalizeP1MinimumDurationMinutes(
          irrigation[P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES]
        );
      if (
        Object.prototype.hasOwnProperty.call(irrigation, P1_SHOT_DURATION_TIMEOUT) &&
        Object.prototype.hasOwnProperty.call(irrigation, P1_VWC_AUTO_SP_NO_INCREASE_SECONDS)
      ) {
        const minP1ShotDurationTimeout = getP1ShotDurationTimeoutMin(irrigation, index);
        irrigation[P1_SHOT_DURATION_TIMEOUT] = Math.max(
          Number(irrigation[P1_SHOT_DURATION_TIMEOUT]),
          minP1ShotDurationTimeout
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(irrigation, P1_WAIT_AFTER_SHOT_MINUTES) &&
        Object.prototype.hasOwnProperty.call(irrigation, P1_SHOT_DURATION_TIMEOUT)
      ) {
        irrigation[P1_WAIT_AFTER_SHOT_MINUTES] = Math.max(
          Number(irrigation[P1_WAIT_AFTER_SHOT_MINUTES]),
          timeoutToMinutes(getEffectiveP1ShotDurationTimeout(irrigation, index))
        );
      }
      if (
        Object.prototype.hasOwnProperty.call(irrigation, P2_WAIT_AFTER_SHOT) &&
        Object.prototype.hasOwnProperty.call(irrigation, P2_SHOT_DURATION_TIMEOUT) &&
        !adaptiveRefillEnabled(irrigation, index)
      ) {
        irrigation[P2_WAIT_AFTER_SHOT] = Math.max(
          Number(irrigation[P2_WAIT_AFTER_SHOT]),
          timeoutToMinutes(getP2ShotDurationTimeout(irrigation))
        );
      }
      irrigation.irrigation_p2_end_before_sunset = irrigationEndTimeForWire(
        irrigation.irrigation_p2_end_before_sunset
      );
    });
    return settings;
  };

  const [displaySavedVWCMax, setDisplaySavedVWCMax] = useState([
    '--- % VWC',
    '--- % VWC',
    '--- % VWC',
    '--- % VWC'
  ]);

  const write_access = controller.access_rights.nutrition.write;

  function hasIrrigationSetting(irrigationIndex, setting) {
    return (
      Object.prototype.hasOwnProperty.call(
        values.irrigation_settings?.[irrigationIndex] ?? {},
        setting
      ) &&
      Object.prototype.hasOwnProperty.call(
        parent.settings.irrigation.irrigation_settings?.[irrigationIndex] ?? {},
        setting
      )
    );
  }

  function setP1ShotDurationTimeout(irrigationIndex, value, saveTemp = true) {
    const timeoutPath = `irrigation_settings[${irrigationIndex}].${P1_SHOT_DURATION_TIMEOUT}`;
    const waitPath = `irrigation_settings[${irrigationIndex}].${P1_WAIT_AFTER_SHOT_MINUTES}`;
    const minValue = getP1ShotDurationTimeoutMin(values.irrigation_settings[irrigationIndex], irrigationIndex);
    const timeoutValue = Math.max(Number(value), minValue);
    const currentWait =
      Number(values.irrigation_settings[irrigationIndex][P1_WAIT_AFTER_SHOT_MINUTES]) || 0;

    if (currentWait < timeoutToMinutes(timeoutValue)) {
      controls.setValue([timeoutPath, waitPath], [timeoutValue, timeoutToMinutes(timeoutValue)], saveTemp);
    } else {
      controls.setValue(timeoutPath, timeoutValue, saveTemp);
    }
  }

  function setP1VwcAutoSpNoIncreaseSeconds(irrigationIndex, value, saveTemp = true) {
    const secondsPath = `irrigation_settings[${irrigationIndex}].${P1_VWC_AUTO_SP_NO_INCREASE_SECONDS}`;
    const timeoutPath = `irrigation_settings[${irrigationIndex}].${P1_SHOT_DURATION_TIMEOUT}`;
    const waitPath = `irrigation_settings[${irrigationIndex}].${P1_WAIT_AFTER_SHOT_MINUTES}`;
    const minTimeout = getP1ShotDurationTimeoutMin({
      ...values.irrigation_settings[irrigationIndex],
      [P1_VWC_AUTO_SP_NO_INCREASE_SECONDS]: value
    }, irrigationIndex);
    const currentTimeout =
      Number(values.irrigation_settings[irrigationIndex][P1_SHOT_DURATION_TIMEOUT]) || 0;
    const currentWait =
      Number(values.irrigation_settings[irrigationIndex][P1_WAIT_AFTER_SHOT_MINUTES]) || 0;

    if (currentTimeout < minTimeout) {
      controls.setValue(
        [secondsPath, timeoutPath, waitPath],
        [value, minTimeout, Math.max(currentWait, timeoutToMinutes(minTimeout))],
        saveTemp
      );
    } else {
      controls.setValue(secondsPath, value, saveTemp);
    }
  }

  function setP1WaitAfterShot(irrigationIndex, value, saveTemp = true) {
    const path = `irrigation_settings[${irrigationIndex}].${P1_WAIT_AFTER_SHOT_MINUTES}`;
    const minValue = timeoutToMinutes(getEffectiveP1ShotDurationTimeout(values.irrigation_settings[irrigationIndex], irrigationIndex));
    controls.setValue(path, Math.max(Number(value), minValue), saveTemp);
  }

  function setP2ShotDurationTimeout(irrigationIndex, value, saveTemp = true) {
    const timeoutPath = `irrigation_settings[${irrigationIndex}].${P2_SHOT_DURATION_TIMEOUT}`;
    const waitPath = `irrigation_settings[${irrigationIndex}].${P2_WAIT_AFTER_SHOT}`;
    const timeoutValue = Math.max(secondsSupported ? 10 : 1, Math.min(Number(value),
      getP2ShotDurationTimeoutMax(values.irrigation_settings[irrigationIndex], irrigationIndex)));
    const currentWait = Number(values.irrigation_settings[irrigationIndex][P2_WAIT_AFTER_SHOT]) || 0;

    if (!adaptiveRefillEnabled(values.irrigation_settings[irrigationIndex], irrigationIndex) &&
        currentWait < timeoutToMinutes(timeoutValue)) {
      controls.setValue([timeoutPath, waitPath], [timeoutValue, timeoutToMinutes(timeoutValue)], saveTemp);
    } else {
      controls.setValue(timeoutPath, timeoutValue, saveTemp);
    }
  }

  function setP2WaitAfterShot(irrigationIndex, value, saveTemp = true) {
    const path = `irrigation_settings[${irrigationIndex}].${P2_WAIT_AFTER_SHOT}`;
    const minValue = getP2WaitMinimumMinutes(values.irrigation_settings[irrigationIndex], irrigationIndex);
    controls.setValue(path, Math.max(Number(value), minValue), saveTemp);
  }

  function setP2AdaptiveDuration(irrigationIndex, setting, value, saveTemp = true) {
    const soak = setting === 'irrigation_p2_refill_soak_seconds';
    const min = soak ? IRRIGATION_P2_SOAK_MIN_SECONDS
      : getP2ShotDurationTimeout(values.irrigation_settings[irrigationIndex]);
    const max = soak ? IRRIGATION_P2_SOAK_MAX_SECONDS : IRRIGATION_P2_TOTAL_MAX_SECONDS;
    const path = `irrigation_settings[${irrigationIndex}].${setting}`;
    const duration = Math.max(min, Math.min(max, Number(value)));
    if (soak && values.irrigation_settings[irrigationIndex][P2_WAIT_AFTER_SHOT] < Math.ceil(duration / 60)) {
      controls.setValue([path, `irrigation_settings[${irrigationIndex}].${P2_WAIT_AFTER_SHOT}`],
        [duration, Math.ceil(duration / 60)], saveTemp);
    } else {
      controls.setValue(path, duration, saveTemp);
    }
  }

  useEffect(() => {
    setDisplaySavedVWCMax(
      values.irrigation_settings.map((s) =>
        s.vwc_auto_sp_max_saved_info === -1 || s.vwc_auto_sp_max_saved_info === 0xffff
          ? '--- % VWC'
          : (s.vwc_auto_sp_max_saved_info / 10).toFixed(1) + ' % VWC'
      )
    );
  }, [values.irrigation_settings.map((s) => s.vwc_auto_sp_max_saved_info).join(',')]);

  const [confirm_action_dialog_infos, setOpenConfirmActionDialog] = React.useState({
    open: false,
    type: '',
    value: undefined
  });
  const openConfirmActionDialog = (type, value) => {
    setOpenConfirmActionDialog({ open: true, type: type, value: value });
  };
  const closeConfirmActionDialog = () => {
    setOpenConfirmActionDialog({ open: false, type: '', value: -1 });
  };

  const [confirm_preset_dialog_infos, setConfirmPresetDialogInfos] = React.useState({
    open: false,
    presetId: 0,
    irrigationIndex: undefined
  });
  const openConfirmPresetDialog = (presetId, irrigationIndex) => {
    if (parseInt(presetId) === 0) return;
    setConfirmPresetDialogInfos({
      open: true,
      presetId: parseInt(presetId),
      irrigationIndex: irrigationIndex
    });
  };
  const closeConfirmPresetDialog = () => {
    setConfirmPresetDialogInfos({ open: false, presetId: 0, irrigationIndex: undefined });
  };

  const [events_used_dialog_open, setEventsUsedDialogsOpen] = useState(false);
  const openEventsUsedDialog = () => {
    setEventsUsedDialogsOpen(true);
  };
  const closeEventsUsedDialog = () => {
    setEventsUsedDialogsOpen(false);
  };

  const [vwcMaxEditDialogInfos, setVwcMaxEditDialogInfos] = useState({
    open: false,
    irrigationIndex: undefined
  });
  const openVwcMaxEditDialog = (irrigationIndex) => {
    setVwcMaxEditDialogInfos({ open: true, irrigationIndex });
  };
  const closeVwcMaxEditDialog = () => {
    setVwcMaxEditDialogInfos({ open: false, irrigationIndex: undefined });
  };
  const setVwcAutoSpMaxSavedInfo = (irrigationIndex, value) => {
    controls.setValue(
      `irrigation_settings[${irrigationIndex}].vwc_auto_sp_max_saved_info`,
      value
    );
    closeVwcMaxEditDialog();
  };

  function makeTimeMin(prefix) {
    // Calculate total time in minutes
    const totalMinutes =
      Number(parent.settings.timing[`${prefix}_hour`]) * 60 +
      Number(parent.settings.timing[`${prefix}_min`]);

    return totalMinutes;
  }

  function parseTimeMin(settings, prefix) {
    if (typeof settings[`${prefix}`] === 'string') {
      const parts = settings[`${prefix}`].split(':').map(Number);

      // Calculate total time in minutes
      const totalMinutes = parts[0] * 60 + parts[1];

      return totalMinutes;
    }

    return settings[`${prefix}`];
  }

  function getDayRangeMin() {
    const start = makeTimeMin('start_day');
    let end = makeTimeMin('end_day');
    // If end is before or equal to start, it means it crosses midnight
    if (end <= start) {
      end += 24 * 60; // add 24 hours in minutes
    }
    return { min: start, max: end };
  }

  function parseDayTimeMin(settings, prefix) {
    const value = parseTimeMin(settings, prefix);
    const { min, max } = getDayRangeMin();

    if (max > 24 * 60 && value < min) {
      return value + 24 * 60;
    }

    return value;
  }

  const getP1LengthMinutes = (irrigationIndex) => {
    let latestEndMinute = 0;

    let counter = 0;
    const irrigationEvents = values.irrigation_events || [];
    for (let i = 0; i < irrigationEvents.length; i++) {
      const evt = irrigationEvents[i];

      if (!evt) continue;
      if (evt.irrigation_index !== parseInt(irrigationIndex)) continue;
      if (evt.phase !== GCSPI_IRRIGATION_TIME_PHASE_P1) continue;
      if (!evt.enabled) continue;

      const eventEndMinute =
        evt.start_minute_from_phase_begin + durationSecondsToRoundedMinutes(evt.duration_seconds);

      if (eventEndMinute > latestEndMinute) {
        latestEndMinute = eventEndMinute;
      }
      counter++;
    }
    return latestEndMinute;
  };

  function formatClockMinutes(totalMinutes) {
    const minutesInDay = 24 * 60;
    const wrappedMinutes =
      ((Math.round(totalMinutes) % minutesInDay) + minutesInDay) % minutesInDay;
    const hours = Math.floor(wrappedMinutes / 60);
    const minutes = wrappedMinutes % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  function formatClockMinutesWithSeconds(totalMinutes) {
    return `${formatClockMinutes(totalMinutes)}:00`;
  }

  function getIrrigationEventClockTime(irrigationIndex, event) {
    const irrigation = values.irrigation_settings[irrigationIndex];
    const startOfDayMinutes = makeTimeMin('start_day');
    const p0LengthMinutes =
      irrigation?.irrigation_p0_enable === 1
        ? Number(irrigation.irrigation_p0_phase_timeout_minutes) || 0
        : 0;
    const p1StartMinutes = startOfDayMinutes + p0LengthMinutes;
    const p2StartMinutes = p1StartMinutes + getP1LengthMinutes(irrigationIndex);
    const phaseStartMinutes =
      event.phase === GCSPI_IRRIGATION_TIME_PHASE_P2 ? p2StartMinutes : p1StartMinutes;

    return formatClockMinutes(
      phaseStartMinutes + (Number(event.start_minute_from_phase_begin) || 0)
    );
  }

  const getP2AllowedStartRange = (irrigationIndex, arr, index) => {
    const irrigation = values.irrigation_settings[irrigationIndex];

    const startOfDayMinutes = makeTimeMin('start_day');
    const endOfDayMinutes = makeTimeMin('end_day');

    const p2EndBeforeSunsetClockMinutes = parseTimeMin(
      irrigation,
      'irrigation_p2_end_before_sunset'
    );

    const dayLengthMinutes = getMinutesDifference(endOfDayMinutes, startOfDayMinutes);
    const endBeforeSunsetMinutes = getMinutesDifference(
      endOfDayMinutes,
      p2EndBeforeSunsetClockMinutes
    );

    const p0LengthMinutes = irrigation.irrigation_p0_enable
      ? irrigation.irrigation_p0_phase_timeout_minutes
      : 0;

    const p1LengthMinutes = getP1LengthMinutes(irrigationIndex);

    const maxStartMinute = Math.max(
      0,
      dayLengthMinutes - p0LengthMinutes - p1LengthMinutes - endBeforeSunsetMinutes
    );

    return maxStartMinute;
  };

  const getPreviousEventEndMinute = (arr, index) => {
    if (index <= 0) return 0;

    const prev = arr[index - 1].evt;
    return (
      Number(prev.start_minute_from_phase_begin || 0) +
      durationSecondsToRoundedMinutes(prev.duration_seconds)
    );
  };

  const removeEvent = (index) => {
    const cur_irrigation_events = values.irrigation_events || [];
    controls.setValue('irrigation_events', [
      ...cur_irrigation_events.filter((evt, i) => i !== index)
    ]);
  };

  function check_event_bleeding(cur, arr, index) {
    if (!cur.enabled) return false;
    let prev = null;
    let next = null;
    // Get first enabled prev event
    for (let i = index - 1; i >= 0; i--) {
      if (arr[i].evt.enabled) {
        prev = arr[i].evt;
        break;
      }
    }
    // Get first enabled next event
    for (let i = index + 1; i < arr.length; i++) {
      if (arr[i].evt.enabled) {
        next = arr[i].evt;
        break;
      }
    }

    if (
      prev &&
      cur.start_minute_from_phase_begin <
        prev.start_minute_from_phase_begin + durationSecondsToRoundedMinutes(prev.duration_seconds)
    ) {
      return true;
    }
    if (
      next &&
      cur.start_minute_from_phase_begin + durationSecondsToRoundedMinutes(cur.duration_seconds) >
        next.start_minute_from_phase_begin
    ) {
      return true;
    }
    return false;
  }

  const addEvent = (irrigation_index, phase) => {
    const all_events = get_events(irrigation_index, phase);
    const prev = all_events[all_events.length - 1];
    const startOffsetMinutes = phase === GCSPI_IRRIGATION_TIME_PHASE_P1 ? 10 : 60;
    const firstStartMinute = phase === GCSPI_IRRIGATION_TIME_PHASE_P2 ? 60 : 0;

    const cur_irrigation_events = values.irrigation_events || [];
    const updated_irrigation_events = [
      ...cur_irrigation_events,
      {
        irrigation_index: irrigation_index,
        phase: phase,
        start_minute_from_phase_begin: prev
          ? prev.evt.start_minute_from_phase_begin + startOffsetMinutes
          : firstStartMinute,
        duration_seconds: prev ? prev.evt.duration_seconds : 180,
        enabled: true
      }
    ];
    controls.setValue('irrigation_events', updated_irrigation_events);
  };

  const saveIrrigationSettings = () => {
    if (controller.connection?.status !== 'good') return;
    if ((values.irrigation_events || []).some(isP1EventStartOutOfBounds)) {
      enqueueSnackbar(t('irrigation_event_start_out_of_bounds'), { variant: 'error' });
      return;
    }

    const submitted = transform_sent_settings(_.cloneDeep(values));
    if (!submitted) {
      enqueueSnackbar(t('irrigation_timeout_settings_invalid'), { variant: 'error' });
      return;
    }
    // Keep automatic slider corrections in the ordinary draft until readback confirms them.
    const correctedPaths = [];
    const correctedValues = [];
    values.irrigation_settings.forEach((irrigation, index) => {
      P2_ADAPTIVE_DURATION_FIELDS.forEach((field) => {
        const path = `irrigation_settings[${index}].${field}`;
        const draftValue = _.get(parent.temp_settings.irrigation, path,
          parent.settings.irrigation.irrigation_settings[index][field]);
        if (irrigation[field] !== draftValue) {
          correctedPaths.push(path);
          correctedValues.push(irrigation[field]);
        }
      });
    });
    if (correctedPaths.length) controls.setValue(correctedPaths, correctedValues);
    // Do not replace committed settings or clear drafts on transport enqueue.
    const zoned = [5155, 6000].includes(Number(controller.type));
    const data = { irrigation: submitted, ...(zoned ? { zone: parent.zone_id } : {}) };
    try {
      const sent = stateManager.wsManager.sendWSMsg(
        controller, 'update_setpoints', data, Number(controller.type) === 6000, zoned
      );
      if (sent !== false) pendingSubmission.current = submitted;
    } catch (_) {
      // Preserve edits for retry after a failed send.
    }
  };

  const get_events = (id, phase) => {
    return (values.irrigation_events || [])
      .map((evt, i) => ({ evt, i }))
      .filter(({ evt }) => evt && evt.irrigation_index === id && evt.phase === phase);
  };

  const { min, max } = getDayRangeMin();

  // Tab handling for irrigation settings
  const irrigationIds = Object.keys(values.irrigation_settings);
  const [activeIrrigation, setActiveIrrigation] = useState(irrigationIds[0] || '');
  useEffect(() => {
    if (!irrigationIds.includes(activeIrrigation) && irrigationIds.length) {
      setActiveIrrigation(irrigationIds[0]);
    }
  }, [irrigationIds, activeIrrigation]);

  const applyIrrigationPreset = (presetId, index) => {
    const preset = irrigationPresets[presetId];
    const { irrigation_settings: presetSettings, irrigation_events: presetEvents } = preset;
    const resolvedPresetSettings = importIrrigationTimeouts(presetSettings, controller);
    if (!resolvedPresetSettings || !write_access) return;
    if (presetId === 1 && adaptiveSupported(index)) {
      Object.assign(resolvedPresetSettings, irrigationAdaptiveDefaults);
    }
    if (presetId === 1 && supportsIrrigationReferenceInterval(controller)) {
      resolvedPresetSettings.irrigation_p1_auto_vwc_exec_gap_days = 0;
    }
    let _values = _.cloneDeep(values);

    if (
      Object.prototype.hasOwnProperty.call(
        resolvedPresetSettings,
        'irrigation_p2_end_before_sunset'
      )
    ) {
      const endDayMinutes = makeTimeMin('end_day');
      if (Number.isFinite(endDayMinutes)) {
        resolvedPresetSettings.irrigation_p2_end_before_sunset =
          formatClockMinutesWithSeconds(endDayMinutes - 120);
      }
    }

    // Update events first
    if (presetEvents) {
      // When updating events, remove ALL events of given index first
      const filteredEvents = (_values.irrigation_events || []).filter(
        (evt) => !evt || evt.irrigation_index !== index
      );
      // Apply events from preset
      const newEvents = presetEvents.map((evt) => ({
        ...evt,
        irrigation_index: index
      }));

      _values.irrigation_events = [...filteredEvents, ...newEvents];
    }

    // Apply irrigation settings
    _values.irrigation_settings[index] = _.merge(
      {},
      _values.irrigation_settings[index],
      resolvedPresetSettings
    );

    // Save irrigation settings and events
    draftActive.current = true;
    setValues(_values);
    controls.saveTempSettings('irrigation_settings', _values.irrigation_settings);
    if (presetEvents) {
      controls.saveTempSettings('irrigation_events', _values.irrigation_events);
    }

    // Send success message to user
    enqueueSnackbar(
      t('applying_irrigation_preset') + ': "' + t(irrigationPresetMapping[presetId]) + '".',
      {
        variant: 'success'
      }
    );
  };

  const renderP2ShotTimeout = (id) => (
    <ShowSetting
      id={`irrigation_settings[${id}].${P2_SHOT_DURATION_TIMEOUT}`}
      show={P2_SHOT_DURATION_TIMEOUT}
      convert_to_hhmmss={secondsSupported}
      min={secondsSupported ? 10 : 1}
      max={getP2ShotDurationTimeoutMax(values.irrigation_settings[id], id)}
      unit={secondsSupported ? ' sec' : ' min'}
      controller={controller}
      cur_value={
        values.irrigation_settings[id][P2_SHOT_DURATION_TIMEOUT]
      }
      controller_value={
        parent.settings.irrigation.irrigation_settings[id][P2_SHOT_DURATION_TIMEOUT]
      }
      controls={{
        ...controls,
        setValue: (_, value, saveTemp = true) =>
          setP2ShotDurationTimeout(id, value, saveTemp)
      }}
      disabled={!write_access}
    />
  );

  const renderP2Wait = (id) => (
    <ShowSetting
      id={`irrigation_settings[${id}].${P2_WAIT_AFTER_SHOT}`}
      show={adaptiveSupported(id) &&
        values.irrigation_settings[id].irrigation_p2_adaptive_enabled === 1
        ? 'irrigation_p2_wait_after_refill_minutes' : P2_WAIT_AFTER_SHOT}
      controller={controller}
      cur_value={Math.max(
        values.irrigation_settings[id][P2_WAIT_AFTER_SHOT],
        getP2WaitMinimumMinutes(values.irrigation_settings[id], id)
      )}
      controller_value={
        parent.settings.irrigation.irrigation_settings[id]
          .irrigation_p2_wait_after_shot_s
      }
      min={getP2WaitMinimumMinutes(values.irrigation_settings[id], id)}
      controls={{
        ...controls,
        setValue: (_, value, saveTemp = true) =>
          setP2WaitAfterShot(id, value, saveTemp)
      }}
      disabled={!write_access}
    />
  );


  return (
    <React.Fragment>
      <ConfirmActionDialog
        stateManager={stateManager}
        open={confirm_action_dialog_infos.open}
        handleClose={closeConfirmActionDialog}
        controller={controller}
        parent={parent}
        action_type={confirm_action_dialog_infos.type}
        action_value={confirm_action_dialog_infos.value}
      />

      <ConfirmIrrigationPresetDialog
        open={confirm_preset_dialog_infos.open}
        handleClose={closeConfirmPresetDialog}
        presetId={confirm_preset_dialog_infos.presetId}
        onApply={() => {
          applyIrrigationPreset(
            confirm_preset_dialog_infos.presetId,
            confirm_preset_dialog_infos.irrigationIndex
          );
          closeConfirmPresetDialog();
        }}
      />

      <EventsUsedDialog
        open={events_used_dialog_open}
        handleClose={closeEventsUsedDialog}
        events={values.irrigation_events}
        irrigationIds={irrigationIds}
      />

      <VwcMaxSavedInfoDialog
        open={vwcMaxEditDialogInfos.open}
        handleClose={closeVwcMaxEditDialog}
        currentValue={
          vwcMaxEditDialogInfos.irrigationIndex !== undefined
            ? values.irrigation_settings[vwcMaxEditDialogInfos.irrigationIndex]
                .vwc_auto_sp_max_saved_info
            : undefined
        }
        onApply={(value) =>
          setVwcAutoSpMaxSavedInfo(vwcMaxEditDialogInfos.irrigationIndex, value)
        }
      />

      <LoadSettingsDialog
        open={openLoadSettingsDialog}
        handleClose={closeLoadSettingsDialog}
        controls={controls}
      />

      <TabContext value={activeIrrigation}>
        <RootZoneAddressSetup controller={controller} wsManager={stateManager.wsManager} />
        <AppBar
          position="static"
          color="default"
          style={
            mobile
              ? {
                  boxSizing: 'border-box',
                  margin: '0 0 16px',
                  padding: '0 8px',
                  width: '100%'
                }
              : { marginBottom: 24 }
          }
        >
          <Box display="flex" justifyContent="center" width="100%">
            <TabList
              variant="scrollable"
              scrollButtons="auto"
              onChange={(_, v) => setActiveIrrigation(v)}
            >
              {irrigationIds.map((iid, idx) => (
                <Tab
                  key={iid}
                  value={iid}
                  label={`${t('irrigation')} ${idx + 1}`}
                  id={`irrigation-tab-${idx}`}
                  aria-controls={`irrigation-tabpanel-${idx}`}
                />
              ))}
            </TabList>
          </Box>
        </AppBar>

        {write_access && (
          <SettingActions
            title={connectionDisabledReason}
            on_default={connectionStatus !== 'good'}
            onReset={controls.resetSettings}
            onSave={saveIrrigationSettings}
          />
        )}

        {irrigationIds.map((id, idx) => (
          <TabPanel key={id} value={id} id={`irrigation-tabpanel-${idx}`} style={{ padding: 0 }}>
            {modernIrrigationStrategiesSupported &&
              SHOW_IRRIGATION_EVENTS_ERROR_BANNER &&
              values.irrigation_load_error !== 0 && (
                <ValueBox style={{ marginTop: `1em` }}>
                  {t('irrigation_events_error_banner')}
                </ValueBox>
              )}
            <ExpandableSettingsGroupGrid
              expand_id={`irrigation_more_irrigation_set_hl`}
              label="more_irrigation_set_hl"
              expand_on_load={true}
            >
              <ValueBox controller={controller} id={`irrigation_settings[${id}].enable_irrigation`}>
                <Typography gutterBottom>
                  <MuiSwitch
                    checked={values.irrigation_settings[id].enable_irrigation === 1}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id].enable_irrigation
                    }
                    onClick={(event) => {
                      controls.setValue(
                        `irrigation_settings[${id}].enable_irrigation`,
                        event.target.checked ? 1 : 0
                      );
                    }}
                    disabled={!write_access}
                    color="primary"
                  />
                  <Desc id="enable_irrigation" />
                </Typography>
              </ValueBox>

              {adaptiveSupported(id) && (
                <ValueBox>
                  <IrrigationActions controller={controller} parent={parent} index={id}
                    wsManager={stateManager.wsManager} />
                </ValueBox>
              )}

              <ValueBox
                controller={controller}
                id={`irrigation_settings[${id}].irrigation_strategy`}
              >
                <Typography gutterBottom>
                  <Desc id="irrigation_p2_strategy" />
                </Typography>
                <FlexSelect
                  id={`irrigation_settings[${id}].irrigation_strategy`}
                  controller={controller}
                  value={values.irrigation_settings[id].irrigation_strategy}
                  controller_value={
                    parent.settings.irrigation.irrigation_settings[id].irrigation_strategy
                  }
                  controls={controls}
                  disabled={!write_access}
                  mapping={availableIrrigationStrategies}
                  use_index={false}
                  onChange={(event) =>
                    controls.setValue(
                      `irrigation_settings[${id}].irrigation_strategy`,
                      parseInt(event.target.value)
                    )
                  }
                />
              </ValueBox>

              {/* Tank information is sent by 5155 controllers starting with API BAE. */}
              {irrigationTankInfoSupported &&
                INDUSTRIAL_IRRIGATION_CONTROLLER_TYPES.includes(controller.type) &&
                hasIrrigationSetting(id, 'selected_tank_id') && (
                  <ValueBox
                    controller={controller}
                    id={`irrigation_settings[${id}].selected_tank_id`}
                  >
                    <Typography gutterBottom>
                      <Desc id="selected_tank_id" />
                    </Typography>
                    <FlexSelect
                      id={`irrigation_settings[${id}].selected_tank_id`}
                      controller={controller}
                      value={values.irrigation_settings[id].selected_tank_id}
                      controller_value={
                        parent.settings.irrigation.irrigation_settings[id].selected_tank_id
                      }
                      controls={controls}
                      disabled={!write_access}
                      mapping={Object.fromEntries([
                        ...irrigationTankInfo
                          .filter(
                            (t) =>
                              isTankEnabled(t) && t.availible_in_irrigation_zones.includes(parseInt(id))
                          )
                          .map((t) => [t.tank_id, t.name]),
                        [255, 'tank_undefined']
                      ])}
                      use_index={false}
                      onChange={(event) =>
                        controls.setValue(
                          `irrigation_settings[${id}].selected_tank_id`,
                          parseInt(event.target.value)
                        )
                      }
                    />
                  </ValueBox>
                )}

              {modernIrrigationStrategiesSupported && (
                <ValueBox
                  controller={controller}
                  id={`irrigation_index_${id}_preset_selection`}
                  show={`irrigation_preset_selection`}
                >
                  <Typography gutterBottom>
                    <Desc id="irrigation_preset_selection" />
                  </Typography>
                  <FlexSelect
                    id={`irrigation_index_${id}_preset_selection_select`}
                    value={0}
                    disabled={!write_access}
                    mapping={irrigationPresetMapping}
                    onChange={(event) => openConfirmPresetDialog(event.target.value, parseInt(id))}
                  />
                </ValueBox>
              )}

              {/* Temporarily hidden until main-controller support is available for type 5155. */}
              {Number(controller.type) !== 5155 &&
                hasIrrigationSetting(id, 'substrate_selection') && (
                <ValueBox
                  controller={controller}
                  id={`irrigation_settings[${id}].substrate_selection`}
                >
                  <Typography gutterBottom>
                    <Desc
                      id={`irrigation_settings[${id}].substrate_selection`}
                      show="substrate_selection"
                    />
                  </Typography>
                  <FlexSelect
                    id={`irrigation_settings[${id}].substrate_selection`}
                    controller={controller}
                    value={String(values.irrigation_settings[id].substrate_selection)}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id].substrate_selection
                    }
                    controls={controls}
                    disabled={!write_access}
                    mapping={substrateSelectionMapping}
                    use_index={false}
                    onChange={(event) =>
                      controls.setValue(
                        `irrigation_settings[${id}].substrate_selection`,
                        parseInt(event.target.value)
                      )
                    }
                  />
                </ValueBox>
                )}

              {isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                // Use Select for Event-Rythmus from Timing-Tab
                <ValueBox>
                  <Typography gutterBottom>
                    <Desc
                      id={`irrigation_settings[${id}].irrigation_time_exec_gap_days`}
                      show="irrigation_time_exec_gap_days"
                    />
                  </Typography>
                  <EventGapSelect
                    maxDays={7}
                    value={values.irrigation_settings[id].irrigation_time_exec_gap_days}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_time_exec_gap_days
                    }
                    onChange={(event, value) =>
                      controls.setValue(
                        `irrigation_settings[${id}].irrigation_time_exec_gap_days`,
                        event.target.value
                      )
                    }
                  />
                </ValueBox>
              )}

              {isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                <ValueBox controller={controller} id={'irrigation_events_used_total'}>
                  <Typography
                    gutterBottom
                    style={{
                      alignItems: 'center',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      whiteSpace: 'normal'
                    }}
                  >
                    <span>
                      <Desc id="irrigation_events_used_total" />
                      {`: ${(values.irrigation_events || []).filter(Boolean).length}/${GCSPI_IRRIGATION_MAX_EVENTS}`}
                    </span>
                    <Button onClick={() => openEventsUsedDialog()}>
                      {t('details')}
                    </Button>
                  </Typography>
                </ValueBox>
              )}
            </ExpandableSettingsGroupGrid>

            <>
              <ExpandableSettingsGroupGrid
                expand_id={`irrigation_phase_0_activation`}
                label="phase_0_activation"
              >
                <CropSteeringP0 className={classes.cropSteering} />
                <></>
                {!isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p0_dry_back_abs`}
                    show={'irrigation_p0_dry_back_abs'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p0_dry_back_abs}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id].irrigation_p0_dry_back_abs
                    }
                    controls={controls}
                    disabled={!write_access}
                  />
                )}

                <ExpandableSettingsGroupGrid
                  expand_id={`irrigation_phase_0_more`}
                  label="more"
                  outside={true}
                >
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p0_phase_timeout_minutes`}
                    show={'irrigation_p0_phase_timeout_minutes'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p0_phase_timeout_minutes}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_p0_phase_timeout_minutes
                    }
                    controls={controls}
                    disabled={
                      values.irrigation_settings[id].irrigation_p0_enable !== 1 ||
                      values.irrigation_settings[id].irrigation_p0_enable_timeout === 0 ||
                      values.irrigation_settings[id].irrigation_p0_enable_timeout === -1 ||
                      !write_access
                    }
                  />
                  <>
                    <ValueBox
                      controller={controller}
                      id={`irrigation_settings[${id}].irrigation_p0_enable`}
                    >
                      <Typography gutterBottom>
                        <MuiSwitch
                          checked={values.irrigation_settings[id].irrigation_p0_enable === 1}
                          controller_value={
                            parent.settings.irrigation.irrigation_settings[id]
                              .irrigation_p0_enable
                          }
                          onClick={(event) => {
                            controls.setValue(
                              `irrigation_settings[${id}].irrigation_p0_enable`,
                              event.target.checked ? 1 : 0
                            );
                          }}
                          disabled={!write_access}
                          color="primary"
                        />
                        <Desc id="irrigation_p0_enable" />
                      </Typography>
                    </ValueBox>
                    {!isIrrigationTimeStrategy(
                      values.irrigation_settings[id].irrigation_strategy
                    ) && (
                      <ValueBox
                        controller={controller}
                        id={`irrigation_settings[${id}].irrigation_p0_enable_timeout`}
                      >
                        <Typography gutterBottom>
                          <MuiSwitch
                            checked={
                              values.irrigation_settings[id].irrigation_p0_enable_timeout === 1
                            }
                            controller_value={
                              parent.settings.irrigation.irrigation_settings[id]
                                .irrigation_p0_enable_timeout
                            }
                            onClick={(event) => {
                              controls.setValue(
                                `irrigation_settings[${id}].irrigation_p0_enable_timeout`,
                                event.target.checked ? 1 : 0
                              );
                            }}
                            disabled={
                              values.irrigation_settings[id].irrigation_p0_enable !== 1 ||
                              !write_access
                            }
                            color="primary"
                          />
                          <Desc id="irrigation_p0_enable_timeout" />
                        </Typography>
                      </ValueBox>
                    )}
                  </>
                </ExpandableSettingsGroupGrid>
              </ExpandableSettingsGroupGrid>

              <ExpandableSettingsGroupGrid
                expand_id={`irrigation_phase_1_ramp_up`}
                label="phase_1_ramp_up"
              >
                <CropSteeringP1 className={classes.cropSteering} />
                <></>
                {isIrrigationVwcStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p1_target_vwc_abs`}
                    show={'irrigation_p1_target_vwc_abs'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p1_target_vwc_abs}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_p1_target_vwc_abs
                    }
                    controls={controls}
                    disabled={!write_access}
                  />
                )}

                {isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ExpandableSettingsGroupGrid
                    expand_id={`irrigation_phase_1_events`}
                    label={`${t('irrigation_events')} (${get_events(parseInt(id), GCSPI_IRRIGATION_TIME_PHASE_P1).length})`}
                    outside={true}
                  >
                    {get_events(parseInt(id), GCSPI_IRRIGATION_TIME_PHASE_P1).map(
                      ({ evt, i }, index, arr) => (
                        <IrrigationEvent
                          key={i}
                          event={evt}
                          savedEvent={parent.settings.irrigation.irrigation_events[i]}
                          index={i}
                          number={index + 1}
                          getClockTime={(startMinute) =>
                            getIrrigationEventClockTime(parseInt(id), {
                              ...evt,
                              start_minute_from_phase_begin: startMinute
                            })
                          }
                          minStart={getPreviousEventEndMinute(arr, index)}
                          maxStart={P1_EVENT_START_MAX_MINUTES}
                          startError={
                            check_event_bleeding(evt, arr, index) ||
                            evt.start_minute_from_phase_begin < getPreviousEventEndMinute(arr, index) ||
                            isP1EventStartOutOfBounds(evt)
                          }
                          durationError={check_event_bleeding(evt, arr, index)}
                          controller={controller}
                          controls={controls}
                          writeAccess={write_access}
                          onDelete={removeEvent}
                        />
                      )
                    )}
                    <ValueBox>
                      <Button
                        onClick={() => addEvent(parseInt(id), GCSPI_IRRIGATION_TIME_PHASE_P1)}
                      >
                        {t('add_irrigation_event')}
                      </Button>
                    </ValueBox>
                  </ExpandableSettingsGroupGrid>
                )}

                {!isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                  !isIrrigationVwcSimpleStrategy(
                    values.irrigation_settings[id].irrigation_strategy
                  ) && (
                    <ExpandableSettingsGroupGrid
                      expand_id={`irrigation_phase_1_more`}
                      label="more"
                      outside={true}
                    >
                      {supportsIrrigationReferenceInterval(controller) &&
                        Number(values.irrigation_settings[id].irrigation_strategy) === 0 && (
                          <ValueBox>
                            <Typography gutterBottom>
                              <Desc id="irrigation_p1_auto_vwc_exec_gap_days" />
                            </Typography>
                            <EventGapSelect
                              maxDays={10}
                              value={values.irrigation_settings[id].irrigation_p1_auto_vwc_exec_gap_days ?? 0}
                              disabled={!write_access}
                              inputProps={{ 'aria-label': t('irrigation_p1_auto_vwc_exec_gap_days') }}
                              onChange={(event) => controls.setValue(
                                `irrigation_settings[${id}].irrigation_p1_auto_vwc_exec_gap_days`,
                                Number(event.target.value)
                              )}
                            />
                          </ValueBox>
                        )}
                      <ShowSetting
                        id={`irrigation_settings[${id}].irrigation_p1_shot_size_vwc_rel`}
                        show={'irrigation_p1_shot_size_vwc_rel'}
                        controller={controller}
                        cur_value={values.irrigation_settings[id].irrigation_p1_shot_size_vwc_rel}
                        controller_value={
                          parent.settings.irrigation.irrigation_settings[id]
                            .irrigation_p1_shot_size_vwc_rel
                        }
                        controls={controls}
                        disabled={!write_access}
                      />
                      {!isIrrigationAutoSimpleStrategy(
                        values.irrigation_settings[id].irrigation_strategy
                      ) && (
                        <ShowSetting
                          id={`irrigation_settings[${id}].irrigation_p1_dry_back_rel`}
                          show={'irrigation_p1_dry_back_rel'}
                          controller={controller}
                          cur_value={values.irrigation_settings[id].irrigation_p1_dry_back_rel}
                          controller_value={
                            parent.settings.irrigation.irrigation_settings[id]
                              .irrigation_p1_dry_back_rel
                          }
                          controls={controls}
                          disabled={!write_access}
                        />
                      )}
                      <ShowSetting
                        id={`irrigation_settings[${id}].${P1_WAIT_AFTER_SHOT_MINUTES}`}
                        show={P1_WAIT_AFTER_SHOT_MINUTES}
                        controller={controller}
                        cur_value={Math.max(
                          values.irrigation_settings[id][P1_WAIT_AFTER_SHOT_MINUTES],
                          timeoutToMinutes(getEffectiveP1ShotDurationTimeout(values.irrigation_settings[id], id))
                        )}
                        controller_value={
                          parent.settings.irrigation.irrigation_settings[id]
                            .irrigation_p1_wait_after_shot_minutes
                        }
                        min={timeoutToMinutes(getEffectiveP1ShotDurationTimeout(values.irrigation_settings[id], id))}
                        controls={{
                          ...controls,
                          setValue: (_, value, saveTemp = true) =>
                            setP1WaitAfterShot(id, value, saveTemp)
                        }}
                        disabled={!write_access}
                      />
                      {!isIrrigationAutoSimpleStrategy(
                        values.irrigation_settings[id].irrigation_strategy
                      ) && (
                        <ShowSetting
                          id={`irrigation_settings[${id}].${P1_SHOT_DURATION_TIMEOUT}`}
                          show={P1_SHOT_DURATION_TIMEOUT}
                          convert_to_hhmmss={secondsSupported}
                          max={secondsSupported ? 1200 : 20}
                          unit={secondsSupported ? ' sec' : ' min'}
                          controller={controller}
                          cur_value={values.irrigation_settings[id][P1_SHOT_DURATION_TIMEOUT]}
                          controller_value={
                            parent.settings.irrigation.irrigation_settings[id][
                              P1_SHOT_DURATION_TIMEOUT
                            ]
                          }
                          min={getP1ShotDurationTimeoutMin(values.irrigation_settings[id], id)}
                          controls={{
                            ...controls,
                            setValue: (_, value, saveTemp = true) =>
                              setP1ShotDurationTimeout(id, value, saveTemp)
                          }}
                          disabled={!write_access}
                        />
                      )}
                      {!isIrrigationAutoSimpleStrategy(
                        values.irrigation_settings[id].irrigation_strategy
                      ) && (
                        <ShowSetting
                          id={`irrigation_settings[${id}].irrigation_p1_phase_timeout_minute`}
                          show={'irrigation_p1_phase_timeout_minute'}
                          controller={controller}
                          cur_value={
                            values.irrigation_settings[id].irrigation_p1_phase_timeout_minute
                          }
                          controller_value={
                            parent.settings.irrigation.irrigation_settings[id]
                              .irrigation_p1_phase_timeout_minute
                          }
                          controls={controls}
                          disabled={!write_access}
                        />
                      )}
                      {p1MinimumCumulativeTimeSupported &&
                        hasIrrigationSetting(id, P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES) &&
                        isIrrigationAdvancedVwcStrategy(
                          values.irrigation_settings[id].irrigation_strategy
                        ) && (
                          <ShowSetting
                            id={`irrigation_settings[${id}].${P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES}`}
                            show={P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES}
                            controller={controller}
                            cur_value={
                              normalizeP1MinimumDurationMinutes(
                                values.irrigation_settings[id][
                                  P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES
                                ]
                              )
                            }
                            controller_value={
                              normalizeP1MinimumDurationMinutes(
                                parent.settings.irrigation.irrigation_settings[id][
                                  P1_MINIMUM_TOTAL_SHOT_DURATION_MINUTES
                                ]
                              )
                            }
                            controls={controls}
                            disabled={!write_access}
                          />
                        )}
                      {isIrrigationAutoStrategy(
                        values.irrigation_settings[id].irrigation_strategy
                      ) && [
                        !isIrrigationAutoSimpleStrategy(
                          values.irrigation_settings[id].irrigation_strategy
                        ) && (
                          <ShowSetting
                            id={`irrigation_settings[${id}].${P1_VWC_AUTO_SP_NO_INCREASE_SECONDS}`}
                            show={adaptiveSupported(id)
                              ? 'irrigation_p1_saturation_confirmation_seconds' : P1_VWC_AUTO_SP_NO_INCREASE_SECONDS}
                            min={adaptiveSupported(id) ? 0 : undefined}
                            unit=" sec"
                            controller={controller}
                            cur_value={
                              values.irrigation_settings[id][P1_VWC_AUTO_SP_NO_INCREASE_SECONDS]
                            }
                            controller_value={
                              parent.settings.irrigation.irrigation_settings[id][
                                P1_VWC_AUTO_SP_NO_INCREASE_SECONDS
                              ]
                            }
                            disabled={!write_access}
                            controls={{
                              ...controls,
                              setValue: (_, value, saveTemp = true) =>
                                setP1VwcAutoSpNoIncreaseSeconds(id, value, saveTemp)
                            }}
                          />
                        ),
                        <ShowSettingMinMax
                          id={`irrigation_settings[${id}].vwc_auto_sp_max_allowed_upper`}
                          show="irrigation_p1_vwc_auto_sp_max_allowed"
                          controller={controller}
                          cur_value_max={
                            values.irrigation_settings[id].vwc_auto_sp_max_allowed_upper
                          }
                          controller_value_max={
                            parent.settings.irrigation.irrigation_settings[id]
                              .vwc_auto_sp_max_allowed_upper
                          }
                          cur_value_min={
                            values.irrigation_settings[id].vwc_auto_sp_max_allowed_lower
                          }
                          controller_value_min={
                            parent.settings.irrigation.irrigation_settings[id]
                              .vwc_auto_sp_max_allowed_lower
                          }
                          min={10}
                          max={100}
                          off_at={-1}
                          controls={controls}
                          type="fan_min_max"
                        />
                      ]}
                    </ExpandableSettingsGroupGrid>
                  )}
              </ExpandableSettingsGroupGrid>

              <ExpandableSettingsGroupGrid
                expand_id={`irrigation_phase_2_maintain`}
                label="phase_2_maintain"
              >
                <CropSteeringP2 className={classes.cropSteering} />
                <></>
                {isIrrigationAutoStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ValueBox controller={controller} id={'vwc_auto_sp_max_saved_info'}>
                    <Typography gutterBottom>
                      <Desc id="vwc_auto_sp_max_saved_info" />
                    </Typography>
                    <span>{displaySavedVWCMax[id]}</span>
                    {write_access && (
                      <EditIcon
                        style={{ cursor: 'pointer', marginLeft: 6, marginBottom: -4 }}
                        onClick={() => openVwcMaxEditDialog(id)}
                      />
                    )}
                  </ValueBox>
                )}

                {isIrrigationAutoStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p2_offset_target_vwc`}
                    show={'irrigation_p2_offset_target_vwc'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p2_offset_target_vwc}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_p2_offset_target_vwc
                    }
                    controls={controls}
                    disabled={!write_access}
                    type="float"
                  />
                )}

                {isIrrigationVwcStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p2_target_vwc_abs`}
                    show={'irrigation_p2_target_vwc_abs'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p2_target_vwc_abs}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_p2_target_vwc_abs
                    }
                    controls={controls}
                    disabled={!write_access}
                  />
                )}

                {!isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p2_dry_back_abs`}
                    show={'irrigation_p2_dry_back_abs'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p2_dry_back_abs}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id].irrigation_p2_dry_back_abs
                    }
                    controls={controls}
                    disabled={!write_access}
                    type="float"
                  />
                )}

                {values.irrigation_settings[id].irrigation_strategy === 2 && (
                  <ExpandableSettingsGroupGrid
                    expand_id={`irrigation_phase_2_events`}
                    label={`${t('irrigation_events')} (${get_events(parseInt(id), GCSPI_IRRIGATION_TIME_PHASE_P2).length})`}
                    outside={true}
                  >
                    {get_events(parseInt(id), GCSPI_IRRIGATION_TIME_PHASE_P2).map(
                      ({ evt, i }, index, arr) => (
                        <IrrigationEvent
                          key={i}
                          event={evt}
                          savedEvent={parent.settings.irrigation.irrigation_events[i]}
                          index={i}
                          number={index + 1}
                          getClockTime={(startMinute) =>
                            getIrrigationEventClockTime(parseInt(id), {
                              ...evt,
                              start_minute_from_phase_begin: startMinute
                            })
                          }
                          minStart={getPreviousEventEndMinute(arr, index)}
                          maxStart={getP2AllowedStartRange(id, arr, index)}
                          startError={
                            check_event_bleeding(evt, arr, index) ||
                            evt.start_minute_from_phase_begin < getPreviousEventEndMinute(arr, index) ||
                            getP2AllowedStartRange(id, arr, index) < evt.start_minute_from_phase_begin
                          }
                          durationError={
                            check_event_bleeding(evt, arr, index) ||
                            getP2AllowedStartRange(id, arr, index) <
                              evt.start_minute_from_phase_begin +
                                durationSecondsToRoundedMinutes(evt.duration_seconds)
                          }
                          controller={controller}
                          controls={controls}
                          writeAccess={write_access}
                          onDelete={removeEvent}
                        />
                      )
                    )}
                    <ValueBox>
                      <Button
                        onClick={() => addEvent(parseInt(id), GCSPI_IRRIGATION_TIME_PHASE_P2)}
                      >
                        {t('add_irrigation_event')}
                      </Button>
                    </ValueBox>
                  </ExpandableSettingsGroupGrid>
                )}

                {values.irrigation_settings[id].irrigation_strategy === 3 && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p2_interval_on_duration_seconds`}
                    show={'irrigation_p2_interval_on_duration_seconds'}
                    controller={controller}
                    cur_value={
                      values.irrigation_settings[id].irrigation_p2_interval_on_duration_seconds
                    }
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_p2_interval_on_duration_seconds
                    }
                    convert_to_hhmmss={true}
                    unit={'sec'}
                    controls={controls}
                    disabled={!write_access}
                  />
                )}

                {values.irrigation_settings[id].irrigation_strategy === 3 && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p2_interval_off_duration_minutes`}
                    show={'irrigation_p2_interval_off_duration_minutes'}
                    controller={controller}
                    cur_value={
                      values.irrigation_settings[id].irrigation_p2_interval_off_duration_minutes
                    }
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id]
                        .irrigation_p2_interval_off_duration_minutes
                    }
                    convert_to_hhmmss={true}
                    unit={'min'}
                    controls={controls}
                    disabled={!write_access}
                  />
                )}

                <ShowSetting
                  id={`irrigation_settings[${id}].irrigation_p2_end_before_sunset`}
                  show={'irrigation_p2_end_before_sunset'}
                  controller={controller}
                  min={min}
                  max={max}
                  cur_value={parseDayTimeMin(
                    values.irrigation_settings[id],
                    'irrigation_p2_end_before_sunset'
                  )}
                  controller_value={parseDayTimeMin(
                    parent.settings.irrigation.irrigation_settings[id],
                    'irrigation_p2_end_before_sunset'
                  )}
                  convert_to_hhmmss={true}
                  wrap_time_over_midnight={true}
                  // convert_to_min={true}
                  unit={'min'}
                  controls={controls}
                  disabled={!write_access}
                />

                {!isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                  !isIrrigationVwcSimpleStrategy(
                    values.irrigation_settings[id].irrigation_strategy
                  ) && (
                    <ExpandableSettingsGroupGrid
                      expand_id={`irrigation_phase_2_more`}
                      label="more"
                      outside={true}
                    >
                      {adaptiveSupported(id) &&
                        isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                          <ValueBox>
                            <Typography gutterBottom>
                              <MuiSwitch
                                checked={values.irrigation_settings[id].irrigation_p2_adaptive_enabled === 1}
                                controller_value={parent.settings.irrigation.irrigation_settings[id].irrigation_p2_adaptive_enabled}
                                inputProps={{ 'aria-label': t('irrigation_p2_adaptive_enabled') }}
                                onChange={(event) => controls.setValue(`irrigation_settings[${id}].irrigation_p2_adaptive_enabled`, event.target.checked ? 1 : 0)}
                                disabled={!write_access} color="primary" />
                              <Desc id="irrigation_p2_adaptive_enabled" />
                            </Typography>
                            <Typography gutterBottom>
                              <MuiSwitch
                                checked={values.irrigation_settings[id].irrigation_p2_learning_enabled === 1}
                                controller_value={parent.settings.irrigation.irrigation_settings[id].irrigation_p2_learning_enabled}
                                inputProps={{ 'aria-label': t('irrigation_p2_learning_enabled') }}
                                onChange={(event) => controls.setValue(`irrigation_settings[${id}].irrigation_p2_learning_enabled`, event.target.checked ? 1 : 0)}
                                disabled={!write_access} color="primary" />
                              <Desc id="irrigation_p2_learning_enabled" />
                            </Typography>
                          </ValueBox>
                        )}
                      {isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                        adaptiveRefillEnabled(values.irrigation_settings[id], id) && (
                          <SettingGroupGrid label="irrigation_p2_adaptive_controls" outside={true}
                            subtitle={t('irrigation_p2_adaptive_controls_text')}>
                            {renderP2ShotTimeout(id)}
                            {['irrigation_p2_refill_soak_seconds', 'irrigation_p2_max_total_watering_seconds'].map((setting) => (
                              <ShowSetting key={setting} id={`irrigation_settings[${id}].${setting}`} show={setting}
                                controller={controller} cur_value={values.irrigation_settings[id][setting]}
                                controller_value={parent.settings.irrigation.irrigation_settings[id][setting]}
                                min={setting === 'irrigation_p2_refill_soak_seconds'
                                  ? IRRIGATION_P2_SOAK_MIN_SECONDS : getP2ShotDurationTimeout(values.irrigation_settings[id])}
                                max={setting === 'irrigation_p2_refill_soak_seconds'
                                  ? IRRIGATION_P2_SOAK_MAX_SECONDS : IRRIGATION_P2_TOTAL_MAX_SECONDS}
                                unit=" sec" convert_to_hhmmss={true}
                                controls={{ ...controls, setValue: (_, value, saveTemp = true) =>
                                  setP2AdaptiveDuration(id, setting, value, saveTemp) }}
                                disabled={!write_access} />
                            ))}
                          </SettingGroupGrid>
                        )}
                      {isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                        adaptiveRefillEnabled(values.irrigation_settings[id], id) && (
                          <SettingGroupGrid label="irrigation_p2_after_target" outside={true}>
                            {renderP2Wait(id)}
                          </SettingGroupGrid>
                        )}
                      {isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                        !adaptiveRefillEnabled(values.irrigation_settings[id], id) && renderP2ShotTimeout(id)}
                      {isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                        !adaptiveRefillEnabled(values.irrigation_settings[id], id) && renderP2Wait(id)}
                      {((adaptiveSupported(id) &&
                        isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy)) ||
                        isIrrigationAutoStrategy(values.irrigation_settings[id].irrigation_strategy)) && (
                        <ValueBox>
                          <Box display="flex" flexWrap="wrap" alignItems="center" style={{ gap: '0.5rem' }}>
                            {adaptiveSupported(id) &&
                              isIrrigationAdvancedVwcStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                                <IrrigationActions controller={controller} parent={parent} index={id}
                                  wsManager={stateManager.wsManager} resetOnly />
                              )}
                            {isIrrigationAutoStrategy(
                              values.irrigation_settings[id].irrigation_strategy
                            ) && (
                              <Box display="flex" alignItems="center">
                                <ActionButton
                                  title={connectionDisabledReason}
                                  disabled={connectionStatus !== 'good'}
                                  onClick={() => openConfirmActionDialog('action_rs_vwc_auto_sp_max', id)}
                                >
                                  {t('rs_vwc_auto_sp_max')}
                                </ActionButton>
                                <InfoButton {...GetHelp('rs_vwc_auto_sp_max')} />
                              </Box>
                            )}
                          </Box>
                        </ValueBox>
                      )}
                    </ExpandableSettingsGroupGrid>
                  )}
              </ExpandableSettingsGroupGrid>

              <ExpandableSettingsGroupGrid
                expand_id={`irrigation_phase_3_drydown`}
                label="phase_3_drydown"
              >
                <CropSteeringP3 className={classes.cropSteering} />
                <></>
                {!isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) && (
                  <ShowSetting
                    id={`irrigation_settings[${id}].irrigation_p3_min_vwc_abs`}
                    show={'irrigation_p3_min_vwc_abs'}
                    controller={controller}
                    cur_value={values.irrigation_settings[id].irrigation_p3_min_vwc_abs}
                    controller_value={
                      parent.settings.irrigation.irrigation_settings[id].irrigation_p3_min_vwc_abs
                    }
                    controls={controls}
                    disabled={!write_access}
                  />
                )}

                {!isIrrigationTimeStrategy(values.irrigation_settings[id].irrigation_strategy) &&
                  !isIrrigationAutoSimpleStrategy(
                    values.irrigation_settings[id].irrigation_strategy
                  ) &&
                  !isIrrigationVwcSimpleStrategy(
                    values.irrigation_settings[id].irrigation_strategy
                  ) && (
                    <ExpandableSettingsGroupGrid
                      expand_id={`irrigation_phase_3_more`}
                      label="more"
                      outside={true}
                    >
                      {secondsSupported && (
                        <ShowSetting
                          id={`irrigation_settings[${id}].${P3_SHOT_DURATION_TIMEOUT}`}
                          show={P3_SHOT_DURATION_TIMEOUT}
                          convert_to_hhmmss={true}
                          controller={controller}
                          cur_value={values.irrigation_settings[id][P3_SHOT_DURATION_TIMEOUT]}
                          controller_value={
                            parent.settings.irrigation.irrigation_settings[id][P3_SHOT_DURATION_TIMEOUT]
                          }
                          min={10}
                          max={3600}
                          unit=" sec"
                          controls={controls}
                          disabled={!write_access}
                        />
                      )}
                      <ShowSetting
                        id={`irrigation_settings[${id}].irrigation_p3_shot_size_rel`}
                        show={'irrigation_p3_shot_size_rel'}
                        controller={controller}
                        cur_value={values.irrigation_settings[id].irrigation_p3_shot_size_rel}
                        controller_value={
                          parent.settings.irrigation.irrigation_settings[id]
                            .irrigation_p3_shot_size_rel
                        }
                        controls={controls}
                        disabled={!write_access}
                      />
                      <ShowSetting
                        id={`irrigation_settings[${id}].irrigation_p3_min_wait_after_shot`}
                        show={'irrigation_p3_min_wait_after_shot'}
                        controller={controller}
                        cur_value={values.irrigation_settings[id].irrigation_p3_min_wait_after_shot}
                        controller_value={
                          parent.settings.irrigation.irrigation_settings[id]
                            .irrigation_p3_min_wait_after_shot
                        }
                        controls={controls}
                        disabled={!write_access}
                      />
                    </ExpandableSettingsGroupGrid>
                  )}
              </ExpandableSettingsGroupGrid>
            </>
          </TabPanel>
        ))}
      </TabContext>

      {write_access && (
        <SettingActions
          title={connectionDisabledReason}
          on_default={connectionStatus !== 'good'}
          onReset={controls.resetSettings}
          onSave={saveIrrigationSettings}
        />
      )}
    </React.Fragment>
  );
}

function ConfirmActionDialog({
  stateManager,
  open,
  handleClose,
  controller,
  parent,
  action_type,
  action_value
}) {
  const { status: connectionStatus, disabledReason: connectionDisabledReason } =
    useControllerConnectionStatus(controller);

  let { t } = useTranslation();

  const doAction = (event) => {
    if (controller.connection?.status !== 'good') return;
    const zone_id = isZoned(controller) ? parent.id : undefined;
    HandleAction(stateManager.wsManager, controller, action_type, action_value, zone_id);
    handleClose();
  };

  return (
    <DialogWithEnterAction
      enterActionDisabled={connectionStatus !== 'good'}
      open={open}
      onClose={handleClose}
      onEnterAction={doAction}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('confirm_action_title')}</DialogTitle>
      <DialogContent>{t(action_type + '_text')}</DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <ActionButton
          title={connectionDisabledReason}
          disabled={connectionStatus !== 'good'}
          onClick={doAction}
          color="primary"
        >
          {t('apply')}
        </ActionButton>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function ConfirmIrrigationPresetDialog({ open, handleClose, presetId, onApply }) {
  let { t } = useTranslation();
  const presetLabel = irrigationPresetMapping[presetId];

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={onApply}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('confirm_irrigation_preset_title')}</DialogTitle>
      <DialogContent>
        <Typography style={{ whiteSpace: 'pre-line' }}>
          {t('confirm_irrigation_preset_text')}
        </Typography>
        {presetLabel && (
          <Typography>
            <i>{t(presetLabel)}</i>
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
        <Button onClick={onApply} color="primary">
          {t('apply')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function EventsUsedDialog({ open, handleClose, events, irrigationIds }) {
  let { t } = useTranslation();
  const safeEvents = events || [];

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={handleClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('irrigation_events_used_hl')}</DialogTitle>
      <DialogContent>
        <Typography>
          {`${t('irrigation_events_used_total')} ${safeEvents.filter(Boolean).length}/${GCSPI_IRRIGATION_MAX_EVENTS}`}
        </Typography>
        {irrigationIds.map((id, idx) => (
          <Typography>
            {`${t('irrigation')} ${idx + 1}: ${safeEvents.filter((evt) => evt && evt.irrigation_index === parseInt(id)).length}`}
          </Typography>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          {t('close')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}

function formatSavedVwcMaxInput(value) {
  if (value === undefined || value === -1 || value === 0xffff) {
    return '';
  }

  return (Number(value) / 10).toFixed(1);
}

function VwcMaxSavedInfoDialog({ open, handleClose, currentValue, onApply }) {
  let { t } = useTranslation();
  const [value, setValue] = React.useState('');
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setValue(formatSavedVwcMaxInput(currentValue));
      setError('');
    }
  }, [open, currentValue]);

  const handleValueChange = (event) => {
    setValue(event.target.value.replace(/[^0-9,.]/g, ''));
    setError('');
  };

  const applyValue = () => {
    const normalizedValue = value.replace(',', '.');

    if (!/^\d+([.]\d*)?$/.test(normalizedValue)) {
      setError(t('invalid_vwc_value'));
      return;
    }

    onApply(Math.round(Number.parseFloat(normalizedValue) * 10));
  };

  return (
    <DialogWithEnterAction
      open={open}
      onClose={handleClose}
      onEnterAction={applyValue}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">{t('vwc_auto_sp_max_saved_info')}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="VWC MAX"
          type="text"
          fullWidth
          value={value}
          onChange={handleValueChange}
          inputProps={{ inputMode: 'decimal', pattern: '[0-9,.]*' }}
        />
        {error}
      </DialogContent>
      <DialogActions>
        <Button onClick={applyValue} color="primary">
          {t('set')}
        </Button>
        <Button onClick={handleClose} color="primary">
          {t('cancel')}
        </Button>
      </DialogActions>
    </DialogWithEnterAction>
  );
}
